import { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Link from "@mui/material/Link";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { apiData } from "../../services/api_service";
import {
  addBundleProductAPI,
  bundleByIdAPI,
  fetchCategoriesDDAPI,
  fetchProductDropdownAPI,
  productAvailabilityAllStoresAPI,
} from "../../config";

import { formatAED } from "../../utils/format";
const emptyLine = {
  product: null,
  quantity: 1,
  unit_price: 0,
  discount_percent: 0,
};

const formatMoney = (value) => formatAED(value);

const lineGrossTotal = (item) =>
  Number(item.quantity || 0) * Number(item.unit_price || 0);

const lineTotal = (item) => {
  const grossTotal = lineGrossTotal(item);
  const discountPercent = Number(item.discount_percent || 0);
  return Math.max(grossTotal - (grossTotal * discountPercent) / 100, 0);
};

const SummaryRow = ({
  label,
  hint,
  value,
  valueColor = "text.primary",
  bold = false,
  muted = false,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: hint ? "flex-start" : "center",
      py: 0.6,
    }}
  >
    <Box>
      <Typography
        variant="body2"
        color={muted ? "text.disabled" : "text.secondary"}
        fontWeight={bold ? 700 : 400}
      >
        {label}
      </Typography>
      {hint ? (
        <Typography
          variant="caption"
          color="text.disabled"
          display="block"
          sx={{ lineHeight: 1.2 }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
    <Typography
      variant="body2"
      fontWeight={bold ? 800 : 600}
      color={muted ? "text.disabled" : valueColor}
    >
      {value}
    </Typography>
  </Box>
);

const BundleProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const alertRef = useRef();
  const user = JSON.parse(localStorage.getItem("user"));
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  // In edit mode, the bundle's already-stored image URL(s). Preserved on save
  // unless the user uploads a replacement.
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  const [form, setForm] = useState({
    product_name: "",
    sku: "", // system-generated bundle number — read-only, never entered by the user
    // Category is optional and multi-select (Item 5) — array of category ids.
    category: [],
    description: "",
    price_mode: "discount_percent",
    discount_value: "",
    limited_time_bundle: false,
    valid_from: null,
    valid_to: null,
    items: [{ ...emptyLine }, { ...emptyLine }],
  });

  const combinedPrice = useMemo(
    () =>
      form.items.reduce(
        (sum, item) => sum + lineTotal(item),
        0
      ),
    [form.items]
  );

  const grossCombinedPrice = useMemo(
    () => form.items.reduce((sum, item) => sum + lineGrossTotal(item), 0),
    [form.items]
  );

  const totalQuantity = useMemo(
    () => form.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [form.items]
  );

  const rowDiscountTotal = Math.max(grossCombinedPrice - combinedPrice, 0);

  const bundleDiscountAmount = useMemo(() => {
    const discount = Number(form.discount_value || 0);
    return Math.max((combinedPrice * discount) / 100, 0);
  }, [combinedPrice, form.discount_value]);

  const bundlePrice = useMemo(() => {
    return Math.max(combinedPrice - bundleDiscountAmount, 0);
  }, [combinedPrice, bundleDiscountAmount]);

  const savings = Math.max(rowDiscountTotal + bundleDiscountAmount, 0);

  const fetchProducts = async (search = "") => {
    setLoadingProducts(true);
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${encodeURIComponent(search)}`
      );
      if (result.success) {
        setProductOptions(result.data.result || []);
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    async function fetchMasters() {
      const categoryResult = await apiData("get", `${fetchCategoriesDDAPI}/213`);
      if (categoryResult.success) {
        setCategories(
          (categoryResult.data.result || []).filter((item) => item.status === 1)
        );
      }
    }
    fetchMasters();
  }, []);

  useEffect(() => {
    if (!image) {
      setImagePreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(image);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  // Edit mode: load the bundle and prefill every field of the form.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const result = await apiData("get", `${bundleByIdAPI}/${id}`);
      if (cancelled) return;
      const bundle = result?.data;
      if (!result?.success || !bundle || Array.isArray(bundle)) {
        alertRef.current?.triggerAlert(
          JSON.stringify({
            message: result?.msg || "Failed to load bundle",
            messageType: "error",
          })
        );
        return;
      }

      let images = [];
      try {
        images = bundle.image ? JSON.parse(bundle.image) : [];
      } catch {
        images = [];
      }
      setExistingImageUrls(Array.isArray(images) ? images : []);

      const items = (bundle.items || []).map((it) => ({
        product: {
          id: it.product_id,
          product_name: it.product_name,
          sku: it.sku,
        },
        quantity:
          it.quantity === null || it.quantity === undefined
            ? ""
            : String(Math.trunc(Number(it.quantity))),
        unit_price: Number(it.unit_price || 0),
        discount_percent: Number(it.discount_percent || 0),
      }));

      const limited = Number(bundle.limited_time_bundle) === 1;
      setForm({
        product_name: bundle.product_name || "",
        sku: bundle.sku || "", // system-generated bundle number, read-only
        category: bundle.category
          ? String(bundle.category)
              .split(",")
              .map((c) => Number(c.trim()))
              .filter((c) => !isNaN(c))
          : [],
        description: bundle.description || "",
        price_mode: bundle.price_mode || "discount_percent",
        discount_value: Number(bundle.discount_value)
          ? String(Number(bundle.discount_value))
          : "",
        limited_time_bundle: limited,
        valid_from: limited && bundle.valid_from ? dayjs(bundle.valid_from) : null,
        valid_to: limited && bundle.valid_to ? dayjs(bundle.valid_to) : null,
        items: items.length ? items : [{ ...emptyLine }, { ...emptyLine }],
      });
      if (limited) setAdvancedOpen(true);
      // if (limited) setAdvancedOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Quantities are capped at 6 digits (999999) — comfortably above any real
  // bundle line, but enough to stop stray keystrokes from producing huge numbers.
  const MAX_LINE_QUANTITY = 999999;

  const updateLine = (index, name, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        if (name !== "quantity") return { ...item, [name]: value };

        // A bundle line can never ask for more than the product's actual
        // available stock (across all active stores) — otherwise the bundle
        // itself would be sellable at a quantity it can't really fulfill.
        // totalAtp is undefined until availability has loaded for this
        // product; fall back to the generic ceiling until then.
        const stockCap =
          item.product?.totalAtp !== undefined
            ? Math.max(Number(item.product.totalAtp) || 0, 0)
            : MAX_LINE_QUANTITY;
        const cap = Math.min(MAX_LINE_QUANTITY, stockCap);

        const nextValue =
          value === ""
            ? ""
            : String(Math.min(cap, Math.max(Math.trunc(Number(value) || 0), 0)));
        return { ...item, quantity: nextValue };
      }),
    }));
  };

  // Item 5: availability across ALL stores for the products currently shown
  // in the picker dropdown, keyed by product id — { totalAtp, byStore }.
  const [availabilityByProduct, setAvailabilityByProduct] = useState({});
  const [availabilityLoadingIds, setAvailabilityLoadingIds] = useState([]);

  const fetchAvailabilityForOptions = async (options) => {
    const toFetch = options.filter(
      (o) => o?.id && availabilityByProduct[o.id] === undefined
    );
    if (!toFetch.length) return;
    setAvailabilityLoadingIds((prev) => [...prev, ...toFetch.map((o) => o.id)]);
    await Promise.all(
      toFetch.map(async (o) => {
        try {
          const res = await apiData(
            "get",
            `${productAvailabilityAllStoresAPI}/${o.id}`
          );
          if (res.success) {
            setAvailabilityByProduct((prev) => ({ ...prev, [o.id]: res.data }));
          }
        } catch (e) {
          console.error("Failed to fetch availability for product", o.id, e);
        } finally {
          setAvailabilityLoadingIds((prev) => prev.filter((id) => id !== o.id));
        }
      })
    );
  };

  useEffect(() => {
    if (productOptions.length) fetchAvailabilityForOptions(productOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOptions]);

  // Edit mode: the bundle's already-saved line items load with just
  // {id, product_name, sku} — no availability, since they didn't come from
  // the search dropdown. Fetch it for them too, then merge it in below,
  // otherwise the Qty cap/"Max N available" text never appears until the
  // user re-picks the product from the dropdown.
  useEffect(() => {
    const loadedProducts = form.items.map((item) => item.product).filter(Boolean);
    if (loadedProducts.length) fetchAvailabilityForOptions(loadedProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items.length, isEditMode]);

  // Once availability comes back (from either effect above), merge it onto
  // any line whose product doesn't have it yet.
  useEffect(() => {
    setForm((prev) => {
      let changed = false;
      const items = prev.items.map((item) => {
        const pid = item.product?.id;
        if (!pid || item.product.totalAtp !== undefined) return item;
        const avail = availabilityByProduct[pid];
        if (!avail) return item;
        changed = true;
        return {
          ...item,
          product: { ...item.product, totalAtp: avail.totalAtp, totalExpired: avail.totalExpired, byStore: avail.byStore },
        };
      });
      return changed ? { ...prev, items } : prev;
    });
  }, [availabilityByProduct]);

  const handleProductSelect = (index, product) => {
    const avail = product ? availabilityByProduct[product.id] : null;
    // Out-of-stock everywhere → not selectable (Item 5).
    if (product && avail && Number(avail.totalAtp) <= 0) {
      alertRef.current?.triggerAlert(
        JSON.stringify({
          message: `${product.product_name} has no available stock in any store and cannot be added to a bundle.`,
          messageType: "error",
        })
      );
      return;
    }
    updateLine(
      index,
      "product",
      product
        ? { ...product, totalAtp: avail?.totalAtp, totalExpired: avail?.totalExpired, byStore: avail?.byStore }
        : null
    );
    updateLine(index, "unit_price", Number(product?.selling_price || 0));
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, { ...emptyLine }] }));
  };

  const removeLine = (index) => {
    setForm((prev) => {
      // Always keep at least one row — an empty item table can't be submitted
      // anyway, and this avoids the confusing "Add Item" reset dance.
      if (prev.items.length <= 1) return prev;
      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      };
    });
  };

  const validate = () => {
    if (!form.product_name.trim()) return "Bundle product name is required";
    // Category is optional (Item 5) — no requirement check.

    const selectedItems = form.items.filter((item) => item.product);
    if (selectedItems.length < 1) {
      return "Select at least one product for the bundle";
    }

    // A product with zero stock across every store cannot be part of a
    // bundle (Item 5).
    if (selectedItems.some((item) => Number(item.product?.totalAtp) <= 0)) {
      return "One or more selected products have no available stock in any store";
    }

    // Backstop for the input's own max cap — catches a quantity typed before
    // availability finished loading, or left over from switching products.
    const overStocked = selectedItems.find(
      (item) =>
        item.product?.totalAtp !== undefined &&
        Number(item.quantity || 0) > Number(item.product.totalAtp)
    );
    if (overStocked) {
      return `${overStocked.product.product_name}: quantity (${overStocked.quantity}) exceeds available stock (${overStocked.product.totalAtp})`;
    }

    const selectedIds = selectedItems.map((item) => item.product.id);
    if (new Set(selectedIds).size !== selectedIds.length) {
      return "Each product can be selected only once";
    }

    if (selectedItems.some((item) => Number(item.quantity || 0) <= 0)) {
      return "Bundle product quantities must be greater than zero";
    }
    if (
      selectedItems.some(
        (item) => !Number.isInteger(Number(item.quantity || 0))
      )
    ) {
      return "Bundle product quantities must be whole numbers";
    }

    // A "bundle" of one product at quantity 1 isn't a bundle. Require at
    // least 2 items total, whether that's 2 different products, one product
    // at quantity 2+, or any mix.
    const totalSelectedQty = selectedItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );
    if (totalSelectedQty < 2) {
      return "A bundle needs at least 2 items total — add another product or increase the quantity.";
    }
    // Bundle line/level discounts removed — a bundle is just a group of
    // products; any discount is applied on the invoice / proforma, not here.

    if (form.limited_time_bundle) {
      if (!form.valid_from || !form.valid_to) {
        return "Valid From and Valid To are required for a limited time bundle";
      }
      if (dayjs(form.valid_to).isBefore(dayjs(form.valid_from), "day")) {
        return "Valid To cannot be before Valid From";
      }
    }

    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const error = validate();
    if (error) {
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: error, messageType: "error" })
      );
      return;
    }

    // A bundle is just a group of products — no pricing/discount is captured
    // here. Price fields are sent as 0 (kept for backend compatibility); the
    // real prices come from the products at invoice / proforma time.
    const items = form.items
      .filter((item) => item.product)
      .map((item) => ({
        product_id: item.product.id,
        quantity: Number(item.quantity || 0),
        unit_price: 0,
        discount_percent: 0,
        line_total: 0,
      }));

    const validFrom =
      form.limited_time_bundle && form.valid_from
        ? dayjs(form.valid_from).startOf("day").format("YYYY-MM-DD HH:mm:ss")
        : "";
    const validTo =
      form.limited_time_bundle && form.valid_to
        ? dayjs(form.valid_to).endOf("day").format("YYYY-MM-DD HH:mm:ss")
        : "";

    const formData = new FormData();
    formData.append("product_name", form.product_name.trim());
    // sku/uom/bar_code intentionally not sent (Item 5) — the bundle number is
    // system-generated on the backend and never entered here.
    // Category is optional and multi-select — sent as a JSON array of ids.
    formData.append("category", JSON.stringify(form.category || []));
    formData.append("description", form.description.trim());
    // price_mode kept as the backend's valid default; discount_value 0 makes it
    // a no-op (bundles carry no pricing — discounts happen on the invoice).
    formData.append("price_mode", "discount_percent");
    formData.append("discount_value", 0);
    formData.append("combined_price", 0);
    formData.append("bundle_price", 0);
    formData.append("limited_time_bundle", form.limited_time_bundle ? 1 : 0);
    formData.append("valid_from", validFrom);
    formData.append("valid_to", validTo);
    if (user?.emp_id != null) {
      formData.append("user_id", user.emp_id);
    }
    formData.append("items", JSON.stringify(items));
    if (image) {
      formData.append("image", image);
    } else if (isEditMode) {
      // No replacement chosen — tell the API to keep the current image.
      formData.append("existing_image", JSON.stringify(existingImageUrls));
    }

    setLoading(true);
    try {
      const result = isEditMode
        ? await apiData("patch", `${bundleByIdAPI}/${id}`, formData)
        : await apiData("post", addBundleProductAPI, formData);
      alertRef.current?.triggerAlert(
        JSON.stringify({
          message: result.msg,
          messageType: result.success ? "success" : "error",
        })
      );
      if (result.success) {
        setTimeout(() => navigate("/product/list"), 1800);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <AlertMessage ref={alertRef} />
      <Grid className="employee-form bold-header">
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography className="page_title">
              {isEditMode ? "Edit Bundle Product" : "Add Bundle Product"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Product</Typography>
              <Typography color="text.primary">Bundle</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} md={6} textAlign="right">
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <form onSubmit={submit}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid #d8e0ea",
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={2.5}>
                Bundle Identity
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={isEditMode ? 4 : 6}>
                  <TextField
                    label="Bundle Product Name"
                    value={form.product_name}
                    onChange={(e) => updateField("product_name", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                {/* Parent SKU / UOM / Barcode removed (Item 5) — the bundle
                    number below is generated by the backend instead. */}
                {isEditMode && (
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      label="Bundle Number"
                      value={form.sku}
                      size="small"
                      fullWidth
                      disabled
                      helperText="System-generated"
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={isEditMode ? 5.5 : 6}>
                  <Autocomplete
                    multiple
                    options={categories}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={categories.filter((item) =>
                      (form.category || []).includes(item.id)
                    )}
                    onChange={(_, value) =>
                      updateField(
                        "category",
                        value.map((v) => v.id)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category (optional)"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={6}
                    sx={{
                      "& .MuiInputBase-root": {
                        alignItems: "flex-start",
                      },
                      "& textarea": {
                        maxHeight: 150,
                        overflow: "auto !important",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>
                    Bundle Image
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AddIcon />}
                      sx={{ borderRadius: 1.5, whiteSpace: "nowrap" }}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) =>
                          setImage(e.currentTarget.files[0] || null)
                        }
                      />
                    </Button>
                    {image && (
                      <Typography variant="body2" color="text.secondary">
                        {image.name}
                      </Typography>
                    )}
                    {(imagePreview || existingImageUrls[0]) && (
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <img
                          src={imagePreview || existingImageUrls[0]}
                          alt="Bundle preview"
                          style={{
                            width: 72,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid #d8e0ea",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            setImage(null);
                            setExistingImageUrls([]);
                          }}
                          sx={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            backgroundColor: "#fff",
                            border: "1px solid #d8e0ea",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: "1px solid #d8e0ea",
                borderRadius: 2,
                backgroundColor: "#fff",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  borderBottom: "1px solid #d8e0ea",
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Bundle Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select existing products, quantity, unit price, and line discount.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addLine}
                  sx={{ whiteSpace: "nowrap", borderRadius: 1.5 }}
                >
                  Add Item
                </Button>
              </Box>

              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "70%", fontWeight: 700 }}>
                        Product
                      </TableCell>
                      <TableCell sx={{ width: 140, fontWeight: 700 }}>
                        Qty
                      </TableCell>
                      <TableCell align="center" sx={{ width: 90, fontWeight: 700 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.items.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#fff" : "#eef7ff",
                          // top-align every cell so the Qty field and delete
                          // button line up with the product Autocomplete's
                          // input row, regardless of whether the availability
                          // caption underneath it makes that cell taller.
                          "& td": { borderColor: "#d8e0ea", py: 1.25, verticalAlign: "top" },
                        }}
                      >
                        <TableCell>
                          <Autocomplete
                            options={productOptions}
                            loading={loadingProducts}
                            getOptionLabel={(option) =>
                              option
                                ? `${option.product_name} (SKU: ${option.sku})`
                                : ""
                            }
                            // Item 5: a product out of stock in every store
                            // cannot be selected for a bundle.
                            getOptionDisabled={(option) => {
                              const avail = availabilityByProduct[option.id];
                              return avail ? Number(avail.totalAtp) <= 0 : false;
                            }}
                            renderOption={(props, option) => {
                              const avail = availabilityByProduct[option.id];
                              const loadingAvail =
                                availabilityLoadingIds.includes(option.id);
                              const outOfStock = avail && Number(avail.totalAtp) <= 0;
                              // Named per-store breakdown, not just a total — the
                              // list is driven entirely by whatever stores
                              // actually have stock, so it scales as stores are
                              // added later without any code change.
                              const storeBreakdown =
                                avail?.byStore
                                  ?.filter((s) => s.atp > 0)
                                  .map((s) => `${s.store_name}: ${s.atp}`)
                                  .join(" · ") || "";
                              return (
                                <li {...props} key={option.id}>
                                  <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
                                    <Typography variant="body2">
                                      {option.product_name} (SKU: {option.sku})
                                    </Typography>
                                    {loadingAvail ? (
                                      <Typography variant="caption" color="text.secondary">
                                        Checking availability…
                                      </Typography>
                                    ) : !avail ? null : outOfStock ? (
                                      <Typography variant="caption" color="error">
                                        {Number(avail.totalExpired) > 0
                                          ? `Out of stock in all stores · ${avail.totalExpired} expired`
                                          : "Out of stock in all stores"}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {storeBreakdown}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              );
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            value={item.product}
                            onInputChange={(_, value, reason) => {
                              if (reason === "input") fetchProducts(value);
                              if (reason === "clear") fetchProducts();
                            }}
                            onChange={(_, value) => handleProductSelect(index, value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select product"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingProducts ? (
                                        <CircularProgress size={18} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                          {item.product?.totalAtp !== undefined && (
                            <Typography
                              variant="caption"
                              color={
                                Number(item.product.totalAtp) <= 0
                                  ? "error"
                                  : "success.main"
                              }
                              sx={{ fontWeight: 600, display: "block" }}
                            >
                              {Number(item.product.totalAtp) <= 0
                                ? Number(item.product.totalExpired) > 0
                                  ? `Out of stock in all stores · ${item.product.totalExpired} expired`
                                  : "Out of stock in all stores"
                                : (item.product.byStore || [])
                                    .filter((s) => s.atp > 0)
                                    .map((s) => `${s.store_name}: ${s.atp}`)
                                    .join(" · ")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              updateLine(index, "quantity", e.target.value)
                            }
                            // Capped at the product's actual available stock
                            // (across all active stores) — can't bundle more
                            // than what's really there.
                            inputProps={{
                              min: 1,
                              max:
                                item.product?.totalAtp !== undefined
                                  ? Math.max(Number(item.product.totalAtp) || 0, 0)
                                  : MAX_LINE_QUANTITY,
                              step: 1,
                              inputMode: "numeric",
                            }}
                            helperText={
                              item.product?.totalAtp !== undefined
                                ? `Max ${item.product.totalAtp} available`
                                : undefined
                            }
                            fullWidth
                          />
                        </TableCell>
                        {/* Unit Price / Disc % / Row Total removed — a bundle
                            carries no pricing; prices apply on the invoice. */}
                        <TableCell align="center">
                          <Tooltip
                            title={
                              form.items.length <= 1
                                ? "At least one item row is required"
                                : ""
                            }
                          >
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => removeLine(index)}
                                disabled={form.items.length <= 1}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {form.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No bundle items added
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {form.items.length > 0 ? (
                      <TableRow
                        sx={{
                          backgroundColor: "#f4f7fb",
                          "& td": {
                            borderTop: "2px solid #0c54a0",
                            borderColor: "#d8e0ea",
                            py: 1.5,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography fontWeight={800}>Items Total</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={800}>
                            {Math.trunc(totalQuantity)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Bundle Pricing card removed — a bundle is just a group of
                products; pricing & discounts happen on the invoice / proforma.
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid #d8e0ea",
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={0.5}>
                Bundle Pricing
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Add an extra discount for buying the bundle. The final price is
                calculated for you.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(220px, 300px) minmax(0, 1fr)",
                  },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    border: "1px dashed #b7c4d6",
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                  }}
                >
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                      Extra bundle discount
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={form.discount_value}
                      onChange={(e) =>
                        updateField("discount_value", e.target.value)
                      }
                      placeholder="0"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, max: 100, step: "0.01" }}
                      sx={{ width: 150 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1.5}
                    >
                      Applied on top of the line-item discounts, on the combined
                      price of all items.
                    </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    border: "1px solid #d8e0ea",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                  }}
                >
                    <SummaryRow
                      label={`Subtotal (${Math.trunc(totalQuantity)} ${
                        Math.trunc(totalQuantity) === 1 ? "item" : "items"
                      })`}
                      value={formatMoney(grossCombinedPrice)}
                    />
                    <SummaryRow
                      label="Line-item discounts"
                      hint="Disc % set per product in the table above"
                      value={`− ${formatMoney(rowDiscountTotal)}`}
                      valueColor="success.main"
                      muted={rowDiscountTotal === 0}
                    />
                    <Divider sx={{ my: 1 }} />
                    <SummaryRow
                      label="Combined price"
                      value={formatMoney(combinedPrice)}
                      bold
                    />
                    <SummaryRow
                      label={`Bundle discount${
                        Number(form.discount_value)
                          ? ` (${Number(form.discount_value)}%)`
                          : ""
                      }`}
                      hint="Extra % on the combined price, set on the left"
                      value={`− ${formatMoney(bundleDiscountAmount)}`}
                      valueColor="success.main"
                      muted={bundleDiscountAmount === 0}
                    />
                    <Divider
                      sx={{
                        my: 1.5,
                        borderBottomWidth: 2,
                        borderColor: "#0c54a0",
                      }}
                    />
                    <SummaryRow
                      label="Total savings"
                      value={formatMoney(savings)}
                      valueColor="success.main"
                      bold
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        mt: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800}>
                          Final bundle price
                        </Typography>
                        {savings > 0 && grossCombinedPrice > 0 ? (
                          <Typography
                            variant="caption"
                            color="success.main"
                            fontWeight={700}
                          >
                            Customer saves{" "}
                            {((savings / grossCombinedPrice) * 100).toFixed(1)}%
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color="primary.main"
                      >
                        {formatMoney(bundlePrice)}
                      </Typography>
                    </Box>
                </Box>
              </Box>
            </Paper>
            */}

            <Accordion
              expanded={advancedOpen}
              onChange={(_, expanded) => setAdvancedOpen(expanded)}
              sx={{
                border: "1px solid #d8e0ea",
                borderRadius: "8px !important",
                boxShadow: "none",
                backgroundColor: "#fff",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={700}>Advanced Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.limited_time_bundle}
                        onChange={(e) =>
                          updateField("limited_time_bundle", e.target.checked)
                        }
                      />
                    }
                    label="Limited Time Bundle"
                  />
                  {form.limited_time_bundle && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <DatePicker
                            label="Valid From"
                            value={form.valid_from}
                            onChange={(value) => updateField("valid_from", value)}
                            inputFormat="DD-MM-YYYY"
                            minDate={dayjs()}
                            maxDate={form.valid_to || undefined}
                            renderInput={(params) => (
                              <TextField {...params} size="small" fullWidth />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DatePicker
                            label="Valid To"
                            value={form.valid_to}
                            onChange={(value) => updateField("valid_to", value)}
                            inputFormat="DD-MM-YYYY"
                            minDate={form.valid_from || dayjs()}
                            renderInput={(params) => (
                              <TextField {...params} size="small" fullWidth />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </LocalizationProvider>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #d8e0ea",
                borderRadius: 2,
                backgroundColor: "#fff",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={loading}
                sx={{ minWidth: 190 }}
              >
                {isEditMode ? "Update Bundle Product" : "Create Bundle Product"}
              </LoadingButton>
            </Paper>
          </Stack>
        </form>
      </Grid>
    </Layout>
  );
};

export default BundleProduct;
